import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { GroundworksAuthLifecycleService, InteractionStatus, MsalBroadcastService, MsalService } from '@groundworks/ngx-groundworks-auth';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { GROUP_ID_TO_ROLE, resetUserRoles, setUserRole } from '../models/roles.model';

// One automatic sign-in per tab, until success.
export const REAUTH_ATTEMPTED_KEY = 'jobs.profile.reauth-attempted';

const GRAPH_SCOPES = ['User.Read'];

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly msalService: MsalService = inject(MsalService);
  private readonly msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
  private readonly authLifecycle = inject(GroundworksAuthLifecycleService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly checkMemberGroupsEndpoint = 'https://graph.microsoft.com/v1.0/me/checkMemberGroups';
  private readonly init$ = new BehaviorSubject<boolean>(false);
  private readonly profileEndpoint = 'https://graph.microsoft.com/v1.0/me';
  readonly profile$ = new BehaviorSubject<ProfileResponse | null>(null);
  private initializedForAccountId: string | null = null;
  private pendingInitialize: Promise<void> | null = null;

  whoami$ = this.profile$.pipe(
    filter((profile): profile is ProfileResponse => !!profile),
    map(profile => profile.displayName)
  );

  async isInitialized(): Promise<void> {
    await firstValueFrom(
      this.init$.pipe(
        filter((v) => v),
        first()
      )
    );
  }

  initialize(): Promise<void> {
    this.pendingInitialize ??= this.load().finally(() => (this.pendingInitialize = null));
    return this.pendingInitialize;
  }

  private async load(): Promise<void> {
    await firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None),
        first()
      )
    );

    const currentAccountId = this.msalService.instance.getActiveAccount()?.homeAccountId ?? null;

    if (this.init$.value) {
      if (this.initializedForAccountId === currentAccountId) return;
      this.init$.next(false);
      this.profile$.next(null);
      resetUserRoles();
    }

    const { accessToken, profile } = await this.loadProfile();
    this.profile$.next(profile);
    await this.applyRoles(accessToken).catch((error) => {
      console.error('Failed to load the user roles:', error);
      throw error;
    });

    this.initializedForAccountId = currentAccountId;
    this.init$.next(true);
  }

  private async loadProfile(): Promise<{ accessToken: string; profile: ProfileResponse }> {
    try {
      const accessToken = await this.getAccountAccessToken();
      clearReauthAttempt();
      return { accessToken, profile: await this.getProfile(accessToken) };
    } catch (error) {
      console.error('Failed to load the Graph profile:', error);
      if (isInteractionRequiredError(error)) await this.signInAgainOnce();
      throw error;
    }
  }

  private async signInAgainOnce(): Promise<void> {
    if (!claimReauthAttempt()) {
      console.warn('Sign-in not retried: already attempted, or session storage unavailable');
      return;
    }
    try {
      const account = this.msalService.instance.getActiveAccount() ?? undefined;
      await this.authLifecycle.login({ account, scopes: GRAPH_SCOPES });
      // Resolving means the redirect was cancelled.
      console.warn('The sign-in redirect was cancelled');
    } catch (error) {
      console.error('Failed to start the sign-in redirect:', error);
    }
    clearReauthAttempt();
  }

  private async applyRoles(accessToken: string): Promise<void> {
    const memberGroupIds = await this.checkMemberGroups(accessToken);
    for (const groupId of memberGroupIds) {
      const role = GROUP_ID_TO_ROLE[groupId];
      if (role) {
        setUserRole(role, true);
      }
    }
  }

  async getProfile(accessToken: string) {
    return await firstValueFrom(
      this.http.get<ProfileResponse>(this.profileEndpoint, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
    );
  }

  async getAccountAccessToken() {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No active account found');
    }
    const accessTokenResult =
      await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: GRAPH_SCOPES,
      });
    return accessTokenResult.accessToken;
  }

  async checkMemberGroups(accessToken: string): Promise<string[]> {
    const groupIds = Object.keys(GROUP_ID_TO_ROLE);

    const response = await firstValueFrom(
      this.http.post<CheckMemberGroupsResponse>(
        this.checkMemberGroupsEndpoint,
        { groupIds },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      )
    );

    return response.value;
  }

  async logout(): Promise<void> {
    try {
      await this.authLifecycle.logout();
    } catch (error) {
      console.error('Logout redirect failed:', error);
    }
  }
}

// Name matching survives duplicate MSAL copies.
function isInteractionRequiredError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const { errorCode } = error as { errorCode?: unknown };
  return error.name === 'InteractionRequiredAuthError' || (error.name === 'BrowserAuthError' && errorCode === 'timed_out');
}

function claimReauthAttempt(): boolean {
  try {
    if (sessionStorage.getItem(REAUTH_ATTEMPTED_KEY) !== null) return false;
    sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, 'true');
    return true;
  } catch {
    return false;
  }
}

function clearReauthAttempt(): void {
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
  } catch {
    // Storage unavailable, so nothing was recorded.
  }
}

interface CheckMemberGroupsResponse {
  value: string[];
}

export interface ProfileResponse {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  userPrincipalName: string;
  mail: string;
  mobilePhone: string;
  jobTitle: string;
  department: string;
  officeLocation: string;
}
